/**
 * <ReleasesPanel> — site-operator Helm release lifecycle UI (#245).
 *
 * Read path is open to all authed roles (the list and status chip surface to
 * every persona). Write actions — Install JBS, Uninstall — are gated to
 * SiteOperator+ via <RoleGate>. The orchestrator BFF (#230) and
 * deployment-service mirror enforce the same boundary on every mutating
 * endpoint, so this gate is a UX convenience, not a security control.
 *
 * Scope per the issue:
 *   - "Install JBS" prominent button when no JBS release exists
 *   - per-release Uninstall button with a confirmation dialog
 *   - status chip per release (installed / installing / failed / uninstalling /
 *     not-installed)
 *   - Engineer-tier features (custom values editor, multi-version diffs,
 *     chart catalog) are explicit anti-goals — keep this surface minimal.
 *
 * The JBS chart preset is hardcoded into the Install button payload
 * (`chartName: 'jbs'`). The deployment-service helm engine looks up
 * `prepareJbsValues()` keyed off that exact name and merges the canonical
 * NodePort + Keycloak OIDC values automatically — there's no need for a
 * separate BFF preset endpoint.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Package, AlertTriangle, X } from 'lucide-react';
// Same workaround as RoleGate.tsx — types package is CommonJS, so rollup's
// named-export analysis trips on `exports.X = X` patterns. Namespace import
// + destructure for runtime values, `import type` for type-only refs.
import { Role } from '@jeket-cloud/types/auth';
import type { HelmRelease, HelmReleaseStatus, SiteNotReadyError } from '@/types';
import { releasesApi } from '@/api/client';
import { RoleGate } from '@/components/auth/RoleGate';
import { parseApiError } from '@/lib/apiError';
import { ReadinessCheckList } from '@/components/deployments/ReadinessChecks';
import { siteReadinessQueryKey } from '@/lib/readiness';

interface Props {
  siteId: string;
}

const POLL_INTERVAL = 10_000;

// ─── Status chip ────────────────────────────────────────────────────────────

interface ChipDescriptor {
  label: string;
  cls: string;
}

function describeStatus(status: HelmReleaseStatus): ChipDescriptor {
  switch (status) {
    case 'deployed':
      return { label: 'Installed', cls: 'text-status-healthy bg-status-healthy/10 border-status-healthy/30' };
    case 'pending-install':
      return { label: 'Installing', cls: 'text-status-warning bg-status-warning/10 border-status-warning/30' };
    case 'pending-upgrade':
      return { label: 'Upgrading', cls: 'text-status-warning bg-status-warning/10 border-status-warning/30' };
    case 'pending-rollback':
      return { label: 'Rolling back', cls: 'text-status-warning bg-status-warning/10 border-status-warning/30' };
    case 'uninstalling':
      return { label: 'Uninstalling', cls: 'text-status-warning bg-status-warning/10 border-status-warning/30' };
    case 'failed':
      return { label: 'Failed', cls: 'text-status-error bg-status-error/10 border-status-error/30' };
    case 'superseded':
      return { label: 'Superseded', cls: 'text-silver-muted bg-surface-elevated border-surface-elevated' };
    case 'uninstalled':
      return { label: 'Uninstalled', cls: 'text-silver-muted bg-surface-elevated border-surface-elevated' };
    case 'unknown':
      return { label: 'Unknown', cls: 'text-silver-muted bg-surface-elevated border-surface-elevated' };
    case 'not_installed':
    default:
      return { label: 'Not installed', cls: 'text-silver-muted bg-surface-elevated border-surface-elevated' };
  }
}

function StatusChip({ status }: { status: HelmReleaseStatus }) {
  const { label, cls } = describeStatus(status);
  return (
    <span
      data-testid={`release-status-${status}`}
      className={`inline-flex items-center text-xs px-2 py-0.5 rounded font-medium border ${cls}`}
    >
      {label}
    </span>
  );
}

// ─── Confirmation dialog ────────────────────────────────────────────────────

interface ConfirmProps {
  title: string;
  body: string;
  confirmLabel: string;
  destructive?: boolean;
  busy?: boolean;
  errorMessage?: string | null;
  /** Extra content under the error (the failing readiness checks). */
  errorDetail?: JSX.Element | null;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmDialog({ title, body, confirmLabel, destructive, busy, errorMessage, errorDetail, onConfirm, onCancel }: ConfirmProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div className="bg-surface border border-surface-elevated rounded-xl shadow-xl p-5 max-w-lg w-full mx-4 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-base font-semibold text-silver-bright">{title}</h2>
          <button
            type="button"
            aria-label="Close"
            data-testid="confirm-dialog-close"
            onClick={onCancel}
            className="text-silver-muted hover:text-silver-bright"
          >
            <X size={16} />
          </button>
        </div>
        <p className="text-sm text-silver-muted">{body}</p>
        {errorMessage && (
          <div className="text-xs text-status-error bg-status-error/10 border border-status-error/30 rounded px-2 py-1">
            {errorMessage}
          </div>
        )}
        {errorDetail}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="confirm-dialog-cancel"
            className="px-3 py-1.5 text-sm font-medium rounded-lg border border-surface-elevated text-silver-bright hover:bg-surface-elevated disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid="confirm-dialog-confirm"
            className={`px-3 py-1.5 text-sm font-medium rounded-lg disabled:opacity-50 ${
              destructive
                ? 'bg-status-error/10 hover:bg-status-error/20 text-status-error border border-status-error/30'
                : 'bg-gold/10 hover:bg-gold/20 text-gold border border-gold/30'
            }`}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Releases query hook (kept inline; only used here) ──────────────────────

function useReleases(siteId: string) {
  return useQuery({
    queryKey: ['cluster-releases', siteId],
    queryFn: () => releasesApi.list(siteId),
    refetchInterval: POLL_INTERVAL,
    staleTime: POLL_INTERVAL,
    enabled: !!siteId,
  });
}

// ─── Readiness refusal ──────────────────────────────────────────────────────

/**
 * The failing checks from a 409 `site_not_ready` (#796), or null for any
 * other failure. POST /releases and POST /releases/:id/install both refuse
 * with it until the site passes every readiness check.
 */
export function siteNotReadyFrom(err: unknown): SiteNotReadyError | null {
  const e = parseApiError(err);
  if (e.status !== 409 || e.code !== 'site_not_ready') return null;
  const body = e.body as Partial<SiteNotReadyError>;
  return {
    error: 'site_not_ready',
    message: body.message ?? 'The site is not ready for JBS.',
    checkedAt: body.checkedAt ?? '',
    checks: Array.isArray(body.checks) ? body.checks : [],
  };
}

// ─── Panel ──────────────────────────────────────────────────────────────────

const JBS_CHART_NAME = 'jbs';
const JBS_RELEASE_NAME = 'jbs';
const JBS_NAMESPACE = 'jbs';

type DialogState =
  | { kind: 'install-jbs' }
  | { kind: 'uninstall'; releaseId: string; releaseName: string }
  | null;

export default function ReleasesPanel({ siteId }: Props) {
  const queryClient = useQueryClient();
  const { data: releases, isLoading, error } = useReleases(siteId);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notReady, setNotReady] = useState<SiteNotReadyError | null>(null);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['cluster-releases', siteId] });
  }

  // Install JBS = create the release definition (chartName: 'jbs') and then
  // POST /install. The deployment-service merges canonical JBS values
  // (NodePort + Keycloak OIDC) on the install path keyed off chartName === 'jbs',
  // so the frontend doesn't need to ship custom values.
  const installJbsMutation = useMutation({
    mutationFn: async () => {
      // If a JBS release definition already exists (e.g. previously created
      // but not yet installed, or uninstalled-but-not-deleted), reuse it.
      const existing = releases?.find((r) => r.chartName === JBS_CHART_NAME);
      const release = existing
        ? existing
        : await releasesApi.create({
            siteId,
            releaseName: JBS_RELEASE_NAME,
            chartName: JBS_CHART_NAME,
            namespace: JBS_NAMESPACE,
          });
      await releasesApi.install(release.id);
    },
    onSuccess: () => {
      setDialog(null);
      setActionError(null);
      setNotReady(null);
      invalidate();
    },
    onError: (err: unknown) => {
      // A readiness refusal names each failing check, its summary and its
      // remediation; show those instead of the raw 409 JSON (#804).
      const refusal = siteNotReadyFrom(err);
      setNotReady(refusal);
      const e = parseApiError(err);
      setActionError(
        refusal
          ? 'JBS can’t be installed yet: this site hasn’t passed every readiness check. Nothing was installed or changed.'
          // The license is checked at install (JSS#864); its message says
          // what the plan is short of and what to do.
          : e.status === 409 && e.code === 'license_insufficient'
            ? `Your Jeket license doesn’t cover this install. ${e.message} Nothing was installed or changed.`
            : e.message || 'Install failed',
      );
      // The refusal re-ran the checks; refresh the Overview panel too.
      queryClient.invalidateQueries({ queryKey: siteReadinessQueryKey(siteId) });
      // A refused install can still have created the release definition.
      invalidate();
    },
  });

  const uninstallMutation = useMutation({
    mutationFn: (releaseId: string) => releasesApi.uninstall(releaseId),
    onSuccess: () => {
      setDialog(null);
      setActionError(null);
      invalidate();
    },
    onError: (err: unknown) => {
      setActionError(parseApiError(err).message || 'Uninstall failed');
    },
  });

  const hasJbs = (releases ?? []).some((r) => r.chartName === JBS_CHART_NAME);

  const installJbsButton = (
    <RoleGate role={Role.SiteOperator}>
      <button
        type="button"
        onClick={() => {
          setActionError(null);
          setNotReady(null);
          setDialog({ kind: 'install-jbs' });
        }}
        disabled={hasJbs && (releases ?? []).some((r) => r.chartName === JBS_CHART_NAME && r.status === 'deployed')}
        data-testid="install-jbs-button"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-gold/10 hover:bg-gold/20 text-gold border border-gold/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Package size={14} />
        Install JBS
      </button>
    </RoleGate>
  );

  const header = (
    <div className="flex items-center justify-between gap-3">
      <h2 className="text-sm font-medium text-silver-muted">Helm Releases</h2>
      <div className="flex items-center gap-2">{installJbsButton}</div>
    </div>
  );

  let body: JSX.Element;
  if (isLoading && !releases) {
    body = (
      <div className="space-y-3">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="bg-surface border border-surface-elevated rounded-xl p-4 animate-pulse">
            <div className="h-4 bg-surface-elevated rounded w-2/3 mb-2" />
            <div className="h-3 bg-surface-elevated rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  } else if (error) {
    body = (
      <div className="bg-surface border border-status-error/30 rounded-xl p-6 text-center">
        <p className="text-status-error text-sm">Failed to load releases</p>
        <p className="text-xs text-silver-muted mt-1">{(error as Error).message}</p>
      </div>
    );
  } else if (!releases || releases.length === 0) {
    body = (
      <div className="bg-surface border border-surface-elevated rounded-xl text-center py-12 space-y-3">
        <Package size={40} className="mx-auto text-silver-muted/30" />
        <p className="text-silver-muted">No Helm releases on this site</p>
        <p className="text-silver-muted/60 text-xs">
          Install JBS to bring up the broadcast stack on this cluster.
        </p>
      </div>
    );
  } else {
    body = (
      <div className="bg-surface border border-surface-elevated rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-surface-elevated">
              <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Release</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Chart</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Namespace</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Status</th>
              <th className="px-4 py-2.5 text-right text-xs font-medium text-silver-muted uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {releases.map((r: HelmRelease) => (
              <tr key={r.id} data-testid={`release-row-${r.id}`} className="border-b border-surface-elevated last:border-b-0">
                <td className="px-4 py-3 text-sm text-silver-bright font-medium">{r.releaseName}</td>
                <td className="px-4 py-3 text-sm text-silver-muted">
                  {r.chartName}
                  {r.chartVersion && <span className="text-silver-muted/60"> @ {r.chartVersion}</span>}
                </td>
                <td className="px-4 py-3 text-sm text-silver-muted">{r.namespace}</td>
                <td className="px-4 py-3"><StatusChip status={r.status} /></td>
                <td className="px-4 py-3 text-right">
                  <RoleGate role={Role.SiteOperator}>
                    {(r.status === 'deployed' || r.status === 'failed') && (
                      <button
                        type="button"
                        onClick={() => {
                          setActionError(null);
                          setDialog({ kind: 'uninstall', releaseId: r.id, releaseName: r.releaseName });
                        }}
                        data-testid={`uninstall-button-${r.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded bg-status-error/10 hover:bg-status-error/20 text-status-error border border-status-error/30"
                      >
                        <AlertTriangle size={12} />
                        Uninstall
                      </button>
                    )}
                  </RoleGate>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const dialogEl = (() => {
    if (!dialog) return null;
    if (dialog.kind === 'install-jbs') {
      return (
        <ConfirmDialog
          title="Install JBS"
          body={`Install the JBS broadcast stack on this site? This deploys the canonical chart (${JBS_CHART_NAME}) into the "${JBS_NAMESPACE}" namespace and may take several minutes.`}
          confirmLabel={notReady ? 'Try again' : 'Install JBS'}
          busy={installJbsMutation.isPending}
          errorMessage={actionError}
          errorDetail={
            notReady && notReady.checks.length > 0 ? (
              <div className="max-h-72 overflow-y-auto border border-surface-elevated rounded-lg p-3" data-testid="install-not-ready-checks">
                <ReadinessCheckList checks={notReady.checks} />
              </div>
            ) : null
          }
          onConfirm={() => installJbsMutation.mutate()}
          onCancel={() => {
            setDialog(null);
            setActionError(null);
            setNotReady(null);
          }}
        />
      );
    }
    return (
      <ConfirmDialog
        title={`Uninstall ${dialog.releaseName}?`}
        body={`This removes the Helm release "${dialog.releaseName}" from the cluster. In-flight viewers will be disconnected. Persistent volumes are retained per the chart's reclaim policy.`}
        confirmLabel="Uninstall"
        destructive
        busy={uninstallMutation.isPending}
        errorMessage={actionError}
        onConfirm={() => uninstallMutation.mutate(dialog.releaseId)}
        onCancel={() => {
          setDialog(null);
          setActionError(null);
        }}
      />
    );
  })();

  return (
    <div className="space-y-4">
      {header}
      {body}
      {dialogEl}
    </div>
  );
}
